import React, { useState, useRef, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth, API_BASE } from '../state/auth.jsx';
import {
    Camera, Plus, Trash2, Pencil, ArrowLeft, List, X,
    AlertTriangle, Upload, Loader2, ChevronRight, MapPin, Sparkles, Copy, Check
} from 'lucide-react';
import LocationPicker from './LocationPicker';
const sparklesIcon = '/sparkle.png';

// iOS-compatible animation styles
const animationStyles = `
    @-webkit-keyframes fadeInScale {
        from {
            opacity: 0;
            -webkit-transform: scale(0.95);
            transform: scale(0.95);
        }
        to {
            opacity: 1;
            -webkit-transform: scale(1);
            transform: scale(1);
        }
    }
    @keyframes fadeInScale {
        from {
            opacity: 0;
            transform: scale(0.95);
        }
        to {
            opacity: 1;
            transform: scale(1);
        }
    }
    @-webkit-keyframes slideInRight {
        from {
            opacity: 0;
            -webkit-transform: translateX(20px);
            transform: translateX(20px);
        }
        to {
            opacity: 1;
            -webkit-transform: translateX(0);
            transform: translateX(0);
        }
    }
    @keyframes slideInRight {
        from {
            opacity: 0;
            transform: translateX(20px);
        }
        to {
            opacity: 1;
            transform: translateX(0);
        }
    }
    .anim-fade-scale {
        -webkit-animation: fadeInScale 0.4s ease-out forwards;
        animation: fadeInScale 0.4s ease-out forwards;
    }
    .anim-slide-right {
        -webkit-animation: slideInRight 0.4s ease-out forwards;
        animation: slideInRight 0.4s ease-out forwards;
    }
`;

const PropertiesManagement = () => {
    const { theme } = useOutletContext();
    const { api, updateToken } = useAuth();
    const isLight = theme === 'light';

    const [subview, setSubview] = useState('selection'); // 'selection' | 'register' | 'portfolio'
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        category: '',
        city: '',
        address: '',
        street_address: '',
        hotel_logo_url: sparklesIcon,
        latitude: null,
        longitude: null
    });
    const [editingId, setEditingId] = useState(null);
    const [showAvatarOptions, setShowAvatarOptions] = useState(false);
    const [isPlusHovered, setIsPlusHovered] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [propertyToDelete, setPropertyToDelete] = useState(null);
    const [confirmValue, setConfirmValue] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [copiedLink, setCopiedLink] = useState(false);
    const [copiedWelcome, setCopiedWelcome] = useState(false);
    const [copiedDirectLink, setCopiedDirectLink] = useState(false);

    const fileInputRef = useRef(null);

    // Fetch properties on mount and when switching to portfolio view
    useEffect(() => {
        if (subview === 'portfolio' || subview === 'selection') {
            fetchProperties();
        }
    }, [subview]);

    const fetchProperties = async () => {
        setLoading(true);
        try {
            const res = await api.get('/properties');
            if (res.data?.success) {
                const props = res.data.properties || [];
                setProperties(props);
                return props;
            }
            return [];
        } catch (error) {
            console.error('Failed to fetch properties:', error);
            return [];
        } finally {
            setLoading(false);
        }
    };

    const handleAction = async () => {
        if (!formData.name || !formData.category || !formData.address) return;

        setLoading(true);
        try {
            if (editingId) {
                // ===== EDITING EXISTING PROPERTY =====
                // For editing, hotel_id already exists in token, so we can upload first
                let logoUrl = formData.hotel_logo_url;
                let fileToUpload = selectedFile;

                // If using the default sparkle icon, ensure it gets uploaded as a file
                if (!fileToUpload && logoUrl === sparklesIcon) {
                    try {
                        const response = await fetch(sparklesIcon);
                        const blob = await response.blob();
                        fileToUpload = new File([blob], 'sparkle.png', { type: 'image/png' });
                    } catch (e) {
                        console.error("Failed to process default icon for upload", e);
                    }
                }

                // Upload file if selected (or generated from default)
                if (fileToUpload) {
                    const uploadFormData = new FormData();
                    uploadFormData.append('avatar', fileToUpload);
                    try {
                        const uploadRes = await api.post('/upload/property-avatar', uploadFormData);
                        if (uploadRes.data?.success) {
                            logoUrl = uploadRes.data.imageUrl;
                        } else {
                            throw new Error(uploadRes.data?.error || 'Upload failed');
                        }
                    } catch (uploadErr) {
                        console.error('Property avatar upload failed:', uploadErr);
                        setLoading(false);
                        return;
                    }
                }

                // Don't send Base64 data to the properties endpoint
                if (typeof logoUrl === 'string' && logoUrl.startsWith('data:')) {
                    console.error('Cannot save Base64 image data');
                    setLoading(false);
                    return;
                }

                const payload = {
                    name: formData.name,
                    category: formData.category,
                    city: formData.city,
                    address: formData.address,
                    street_address: formData.street_address,
                    hotel_logo_url: logoUrl,
                    latitude: formData.latitude,
                    longitude: formData.longitude
                };

                const res = await api.put(`/properties/${editingId}`, payload);
                if (res.data?.success) {
                    await fetchProperties();
                    setSubview('portfolio');
                    resetForm();
                }
            } else {
                // ===== CREATING NEW PROPERTY =====
                // For new properties, create property first to get hotel_id token,
                // then upload avatar, then update property with avatar URL

                // Step 1: Create property with default/sparkle icon first
                const initialPayload = {
                    name: formData.name,
                    category: formData.category,
                    city: formData.city,
                    address: formData.address,
                    street_address: formData.street_address,
                    hotel_logo_url: sparklesIcon, // Use default icon initially
                    latitude: formData.latitude,
                    longitude: formData.longitude
                };

                const createRes = await api.post('/properties', initialPayload);
                if (!createRes.data?.success) {
                    throw new Error('Failed to create property');
                }

                // Step 2: Update token with new hotel_id
                if (createRes.data?.accessToken) {
                    await updateToken(createRes.data.accessToken);
                }

                const newPropertyId = createRes.data?.property?.id;

                // Step 3: Upload avatar if user selected one (now we have hotel_id in token)
                let fileToUpload = selectedFile;
                let finalLogoUrl = sparklesIcon;

                // If using the default sparkle icon, prepare it for upload
                if (!fileToUpload && formData.hotel_logo_url === sparklesIcon) {
                    try {
                        const response = await fetch(sparklesIcon);
                        const blob = await response.blob();
                        fileToUpload = new File([blob], 'sparkle.png', { type: 'image/png' });
                    } catch (e) {
                        console.error("Failed to process default icon for upload", e);
                    }
                }

                // Upload the avatar
                if (fileToUpload) {
                    const uploadFormData = new FormData();
                    uploadFormData.append('avatar', fileToUpload);
                    try {
                        const uploadRes = await api.post('/upload/property-avatar', uploadFormData);
                        if (uploadRes.data?.success) {
                            finalLogoUrl = uploadRes.data.imageUrl;
                        }
                    } catch (uploadErr) {
                        console.error('Property avatar upload failed:', uploadErr);
                        // Don't fail the whole operation, we already created the property
                        // Just log and continue with default icon
                    }
                }

                // Step 4: Update property with the uploaded avatar URL
                if (newPropertyId && finalLogoUrl !== sparklesIcon) {
                    const updatePayload = {
                        name: formData.name,
                        category: formData.category,
                        city: formData.city,
                        address: formData.address,
                        street_address: formData.street_address,
                        hotel_logo_url: finalLogoUrl,
                        latitude: formData.latitude,
                        longitude: formData.longitude
                    };

                    try {
                        await api.put(`/properties/${newPropertyId}`, updatePayload);
                    } catch (updateErr) {
                        console.error('Failed to update property with avatar:', updateErr);
                        // Property is created, just avatar update failed - don't throw error
                    }
                }

                await fetchProperties();
                setSubview('portfolio');
                resetForm();
            }
        } catch (error) {
            console.error('Failed to save property:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file); // Store file for upload
            const reader = new FileReader();
            reader.onloadend = () => {
                setFormData({ ...formData, hotel_logo_url: reader.result }); // Preview only
                setShowAvatarOptions(false);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSetDefaultAvatar = (e) => {
        e.stopPropagation();
        setFormData({ ...formData, hotel_logo_url: sparklesIcon });
        setSelectedFile(null);
        setShowAvatarOptions(false);
    };

    const renderAvatarContent = (image, size = 'full') => {
        if (!image) {
            return (
                <div className="opacity-20 flex flex-col items-center justify-center w-full h-full">
                    <Camera size={size === 'full' ? 40 : 24} />
                </div>
            );
        }



        let displayImage = image;
        // Resolve absolute URL if it is a relative path from the API
        if (typeof displayImage === 'string' && displayImage.startsWith('/uploads')) {
            try {
                displayImage = `${new URL(API_BASE).origin}${displayImage}`;
            } catch (e) {
                console.error("Invalid API_BASE or image path", e);
            }
        }

        return (
            <img
                src={displayImage}
                alt="Avatar"
                className="h-full w-full object-cover rounded-full"
                onError={(e) => console.error("Error loading property image:", displayImage, e)}
            />
        );
    };

    const startEdit = (p) => {
        setEditingId(p.id);
        setFormData({
            name: p.name,
            category: p.category || p.type || '',
            city: p.city || '',
            address: p.address || '',
            street_address: p.street_address || '', // New field for short address
            hotel_logo_url: p.hotel_logo_url || sparklesIcon,
            latitude: p.latitude || null,
            longitude: p.longitude || null
        });
        setSubview('register');
    };

    const openDeleteModal = (p) => {
        setPropertyToDelete(p);
        setShowDeleteModal(true);
        setConfirmValue('');
    };

    const confirmDelete = async () => {
        if (confirmValue.toUpperCase() === 'DELETE') {
            setLoading(true);
            try {
                const res = await api.delete(`/properties/${propertyToDelete.id}`);

                // Update token if backend returns a new one
                if (res.data?.accessToken) {
                    await updateToken(res.data.accessToken);
                } else if (res.data?.accessToken === null) {
                    // No properties left - backend returned null token
                    console.log('No properties remaining');
                }

                await fetchProperties();
                setShowDeleteModal(false);
                setPropertyToDelete(null);
                setConfirmValue('');
                resetForm();

                // If no properties left, go to selection view
                const updatedProperties = await fetchProperties();
                if (!updatedProperties || updatedProperties.length === 0) {
                    setSubview('selection');
                } else {
                    setSubview('portfolio');
                }
            } catch (error) {
                console.error('Failed to delete property:', error);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleBack = () => {
        if (editingId) {
            resetForm();
            setSubview('portfolio');
        } else {
            setSubview('selection');
        }
    };

    const copyLink = async (propertyId) => {
        const link = `https://chat.kypser.com?propertyId=${propertyId}`;
        try {
            await navigator.clipboard.writeText(link);
            setCopiedLink(propertyId);
            setTimeout(() => setCopiedLink(false), 2000);
        } catch (err) {
            console.error('Failed to copy link:', err);
        }
    };

    const copyWelcomeMessage = async () => {
        if (!editingId) return;

        // Find property to get the UI ID
        const property = properties.find(p => p.id === editingId);
        const uiId = property?.ui_id;

        if (!uiId) {
            console.error('Could not find property UI ID');
            return;
        }

        const link = `https://chat.kypser.com?propertyId=${uiId}`;
        const message = `Welcome!

During your stay, you'll have access to a smart digital concierge that can answer questions instantly - anytime, day or night.

From check-in & Wi-Fi to local recommendations, just tap below:

${link}

We're always here if you need anything.

Enjoy your stay`;

        try {
            await navigator.clipboard.writeText(message);
            setCopiedWelcome(true);
            setTimeout(() => setCopiedWelcome(false), 2000);
        } catch (err) {
            console.error('Failed to copy welcome message:', err);
        }
    };

    const copyDirectLink = async () => {
        if (!editingId) return;

        // Find property to get the UI ID
        const property = properties.find(p => p.id === editingId);
        const uiId = property?.ui_id;

        if (!uiId) {
            console.error('Could not find property UI ID');
            return;
        }

        const link = `https://chat.kypser.com?propertyId=${uiId}`;

        try {
            await navigator.clipboard.writeText(link);
            setCopiedDirectLink(true);
            setTimeout(() => setCopiedDirectLink(false), 2000);
        } catch (err) {
            console.error('Failed to copy direct link:', err);
        }
    };

    const resetForm = () => {
        setEditingId(null);
        setFormData({ name: '', category: '', city: '', address: '', street_address: '', hotel_logo_url: sparklesIcon, latitude: null, longitude: null });
        setSelectedFile(null);
    };

    useEffect(() => {
        const handleClickOutside = () => {
            if (showAvatarOptions) setShowAvatarOptions(false);
        };
        if (showAvatarOptions) {
            window.addEventListener('click', handleClickOutside);
        }
        return () => window.removeEventListener('click', handleClickOutside);
    }, [showAvatarOptions]);

    // Selection View
    if (subview === 'selection') {
        return (
            <>
                <style>{animationStyles}</style>
                <div className="min-h-full flex flex-col justify-start sm:justify-center py-8 sm:py-0 max-w-5xl mx-auto space-y-6 sm:space-y-8 anim-fade-scale">
                    <div className="text-center space-y-2 mb-2 sm:mb-4 px-4">
                        <h2 className={`text-2xl sm:text-4xl font-bold ${isLight ? 'text-neutral-900' : 'text-white'}`}>
                            Property Management
                        </h2>
                        <p className={`text-sm sm:text-base font-semibold ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                            Choose an action to manage your properties
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8 px-4">
                        <SelectionCard
                            isLight={isLight}
                            icon={<Plus size={40} />}
                            title="Add Property"
                            desc="Register a new property and set up its identity."
                            onClick={() => setSubview('register')}
                        />
                        <SelectionCard
                            isLight={isLight}
                            icon={<List size={40} />}
                            title="Your Properties"
                            desc="View and manage your existing properties."
                            onClick={() => setSubview('portfolio')}
                            badge={`${properties.length} Active`}
                        />
                    </div>
                </div>
            </>
        );
    }

    // Register/Edit View
    if (subview === 'register') {
        const currentProperty = editingId ? properties.find(p => p.id === editingId) : null;
        const displayUiId = currentProperty?.ui_id;

        return (
            <>
                <style>{animationStyles}</style>
                {showDeleteModal && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-black/60 backdrop-blur-md" onClick={() => setShowDeleteModal(false)} />
                        <div className={`relative w-full max-w-md rounded-3xl border p-10 shadow-2xl animate-in zoom-in-95 duration-300 ${isLight ? 'bg-white border-neutral-200' : 'bg-[#0C0F16] border-white/10'
                            }`}>
                            <div className="flex flex-col items-center text-center space-y-4">
                                <div className="h-16 w-16 rounded-2xl bg-red-500/10 text-red-500 flex items-center justify-center mb-2">
                                    <AlertTriangle size={32} />
                                </div>
                                <h3 className={`text-2xl font-bold ${isLight ? 'text-neutral-900' : 'text-white'}`}>
                                    Delete Property?
                                </h3>
                                <p className={`font-semibold ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                                    You are about to remove <span className={isLight ? 'text-neutral-900' : 'text-white'}>"{propertyToDelete?.name}"</span>.
                                </p>

                                <div className="w-full space-y-4 pt-4">
                                    <div className="space-y-2">
                                        <label className={`text-xs uppercase tracking-wider font-bold block text-left px-1 ${isLight ? 'text-neutral-400' : 'text-neutral-500'
                                            }`}>
                                            Type "DELETE" to confirm
                                        </label>
                                        <input
                                            type="text"
                                            value={confirmValue}
                                            onChange={(e) => setConfirmValue(e.target.value)}
                                            placeholder="DELETE"
                                            className={`w-full p-4 rounded-2xl border bg-transparent font-bold outline-none transition-all text-center ${isLight ? 'border-neutral-300 focus:border-red-500 text-neutral-900' : 'border-white/10 focus:border-red-500 text-white'
                                                }`}
                                        />
                                    </div>

                                    <div className="flex flex-col gap-3">
                                        <button
                                            onClick={confirmDelete}
                                            disabled={confirmValue.toUpperCase() !== 'DELETE' || loading}
                                            className={`w-full py-4 rounded-2xl font-bold transition-all shadow-lg flex items-center justify-center gap-2 ${confirmValue.toUpperCase() === 'DELETE' && !loading
                                                ? 'bg-red-600 text-white shadow-red-600/20'
                                                : 'bg-neutral-500 text-white/50 cursor-not-allowed opacity-40'
                                                }`}
                                        >
                                            {loading && <Loader2 size={18} className="animate-spin" />}
                                            Delete Now
                                        </button>
                                        <button
                                            onClick={() => setShowDeleteModal(false)}
                                            className={`w-full py-4 rounded-2xl font-bold ${isLight ? 'bg-neutral-100' : 'bg-white/5'
                                                }`}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <div key="register-view" className="h-full w-full flex flex-col">
                    {/* Back Button - fixed above scroll area */}
                    <div className="max-w-4xl mx-auto w-full px-4 sm:px-0 py-4">
                        <button
                            onClick={handleBack}
                            className={`flex items-center gap-2 font-bold text-sm transition-colors ${isLight ? 'text-neutral-500 hover:text-neutral-900' : 'text-neutral-400 hover:text-white'
                                }`}
                        >
                            <ArrowLeft size={16} /> Back
                        </button>
                    </div>

                    {/* Scrollable content area */}
                    <div className="flex-1 min-h-0 overflow-y-auto hidden-scrollbar">
                        <div className="max-w-4xl mx-auto px-4 sm:px-0 anim-slide-right pb-32 pt-4">

                            <div className={`rounded-3xl border p-4 sm:p-12 backdrop-blur-xl shadow-xl ${isLight ? 'bg-white/80 border-neutral-200' : 'bg-white/5 border-white/5'
                                }`}>
                                <div className="flex items-center justify-between mb-10">
                                    <div>
                                        <h3 className={`text-3xl font-bold ${isLight ? 'text-neutral-900' : 'text-white'}`}>
                                            {editingId ? 'Edit Property' : 'New Property'}
                                        </h3>
                                        <p className={`text-sm font-semibold mt-1 ${isLight ? 'text-neutral-500' : 'text-neutral-400'
                                            }`}>
                                            Define the identity for your AI Concierge
                                        </p>
                                    </div>
                                </div>

                                <div className="space-y-12">
                                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
                                        {/* Avatar Upload */}
                                        <div className="flex flex-col items-center space-y-6">
                                            <div className="relative">
                                                <div className={`h-48 w-48 rounded-full border-4 border-dashed flex items-center justify-center overflow-hidden transition-all shadow-2xl ${isLight
                                                    ? (isPlusHovered ? 'bg-neutral-50 border-neutral-900' : 'bg-neutral-50 border-neutral-200')
                                                    : (isPlusHovered ? 'bg-white/5 border-white' : 'bg-white/5 border-white/10')
                                                    }`}>
                                                    {renderAvatarContent(formData.hotel_logo_url)}
                                                </div>

                                                <div className="absolute bottom-2 right-2 z-20">
                                                    <button
                                                        onMouseEnter={() => setIsPlusHovered(true)}
                                                        onMouseLeave={() => setIsPlusHovered(false)}
                                                        onClick={(e) => { e.stopPropagation(); setShowAvatarOptions(!showAvatarOptions); }}
                                                        className={`h-12 w-12 rounded-full flex items-center justify-center shadow-2xl border-4 transition-all hover:scale-110 active:scale-95 ${isLight
                                                            ? 'bg-neutral-900 text-white border-white'
                                                            : 'bg-white text-black border-[#0A0C10]'
                                                            }`}
                                                    >
                                                        <Plus size={20} className={`transition-transform ${showAvatarOptions ? 'rotate-45' : ''}`} />
                                                    </button>

                                                    {showAvatarOptions && (
                                                        <div
                                                            onClick={(e) => e.stopPropagation()}
                                                            className={`absolute bottom-14 right-0 w-56 rounded-2xl border p-2 shadow-2xl animate-in fade-in slide-in-from-bottom-2 duration-200 backdrop-blur-xl ${isLight ? 'bg-white border-neutral-200' : 'bg-[#14181F] border-white/10'
                                                                }`}
                                                        >
                                                            <button
                                                                onClick={handleSetDefaultAvatar}
                                                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-colors ${isLight ? 'hover:bg-neutral-100 text-neutral-900' : 'hover:bg-white/5 text-white'
                                                                    }`}
                                                            >
                                                                <Sparkles size={18} className="text-amber-400" />
                                                                Default Avatar
                                                            </button>
                                                            <button
                                                                onClick={() => fileInputRef.current.click()}
                                                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-colors ${isLight ? 'hover:bg-neutral-100 text-neutral-900' : 'hover:bg-white/5 text-white'
                                                                    }`}
                                                            >
                                                                <Upload size={18} className="opacity-60" />
                                                                Upload Custom
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                                <input type="file" ref={fileInputRef} hidden accept="image/*" onChange={handleImageChange} />
                                            </div>
                                            <div className="text-center">
                                                <h4 className={`text-xs font-bold uppercase tracking-wider ${isLight ? 'text-neutral-600' : 'text-neutral-400'
                                                    }`}>
                                                    Identity Avatar
                                                </h4>
                                                <p className={`text-xs mt-1 font-semibold ${isLight ? 'text-neutral-400' : 'text-neutral-500'
                                                    }`}>
                                                    Visible in customer chat
                                                </p>
                                            </div>
                                        </div>

                                        {/* Chat Preview */}
                                        <div className="lg:col-span-2">
                                            <div className={`rounded-3xl border p-4 sm:p-8 space-y-8 transition-all h-full flex flex-col justify-center ${isLight ? 'bg-neutral-50 border-neutral-200' : 'bg-white/5 border-white/5'
                                                }`}>
                                                <div className="flex flex-col items-end space-y-2">
                                                    <div className={`p-4 px-6 rounded-2xl rounded-tr-none font-semibold text-sm shadow-sm ${isLight ? 'bg-neutral-900 text-white' : 'bg-white text-black'
                                                        }`}>
                                                        "Hi! What time is check-out?"
                                                    </div>
                                                    <p className={`text-xs font-bold uppercase tracking-wider px-1 ${isLight ? 'text-neutral-400' : 'text-neutral-500'
                                                        }`}>
                                                        Guest
                                                    </p>
                                                </div>

                                                <div className="flex items-start gap-4">
                                                    <div className={`h-14 w-14 rounded-full flex-shrink-0 overflow-hidden border shadow-xl flex items-center justify-center ${isLight ? 'bg-white border-neutral-200' : 'bg-black border-white/10'
                                                        }`}>
                                                        {renderAvatarContent(formData.hotel_logo_url, 'sm')}
                                                    </div>
                                                    <div className="space-y-2 flex-1 max-w-[85%]">
                                                        <p className={`text-xs font-bold uppercase tracking-wider ${isLight ? 'text-neutral-400' : 'text-neutral-500'
                                                            }`}>
                                                            {formData.name || 'Concierge AI'}
                                                        </p>
                                                        <div className={`p-6 rounded-2xl rounded-tl-none font-semibold text-sm shadow-sm border ${isLight ? 'bg-white border-neutral-200 text-neutral-900' : 'bg-white/10 border-white/5 text-white'
                                                            }`}>
                                                            "Check-out is by 11:00 am. Please place the keys back in the lockbox and scramble the code before leaving."
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Form Fields */}
                                    <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 pt-12 border-t ${isLight ? 'border-neutral-200' : 'border-white/5'
                                        }`}>
                                        <FormInput
                                            label="Property Name"
                                            value={formData.name}
                                            onChange={(v) => setFormData({ ...formData, name: v })}
                                            isLight={isLight}
                                            placeholder="Grand Hotel"
                                        />
                                        <FormInput
                                            label="Category"
                                            value={formData.category}
                                            onChange={(v) => setFormData({ ...formData, category: v })}
                                            isLight={isLight}
                                            placeholder="Resort"
                                        />

                                        <div className="md:col-span-2 space-y-3">
                                            <label className={`text-xs uppercase tracking-wider font-bold px-1 ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`}>
                                                Property Address
                                            </label>
                                            <div className={`rounded-3xl border overflow-hidden p-1 ${isLight ? 'bg-neutral-50 border-neutral-200' : 'bg-white/5 border-white/5'}`}>
                                                <LocationPicker
                                                    isLight={isLight}
                                                    initialLat={formData.latitude}
                                                    initialLon={formData.longitude}
                                                    initialAddress={formData.address}
                                                    onLocationConfirm={(loc) => {
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            latitude: loc.lat,
                                                            longitude: loc.lon,
                                                            address: loc.address, // Now full
                                                            street_address: loc.street_address,
                                                            city: loc.city || prev.city,
                                                        }));
                                                    }}
                                                />
                                            </div>

                                        </div>

                                    </div>

                                    {/* Share with Guests */}
                                    {editingId && displayUiId && (
                                        <div className={`pt-8 border-t ${isLight ? 'border-neutral-200' : 'border-white/5'}`}>
                                            <div className="flex flex-col xl:flex-row gap-8 items-start">
                                                <div className="flex-1">
                                                    <h3 className={`text-lg font-bold mb-2 ${isLight ? 'text-neutral-900' : 'text-white'}`}>Share with Guests</h3>
                                                    <p className={`text-sm mb-6 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                                                        Copy this welcome message to send to your guests before their arrival. It includes a smart link to your AI concierge.
                                                    </p>

                                                    <button
                                                        onClick={copyWelcomeMessage}
                                                        className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold transition-all shadow-lg active:scale-95 ${copiedWelcome
                                                            ? 'bg-green-500 text-white shadow-green-500/20'
                                                            : isLight
                                                                ? 'bg-neutral-900 text-white shadow-neutral-900/20 hover:bg-neutral-800'
                                                                : 'bg-white text-black shadow-white/10 hover:bg-neutral-200'
                                                            }`}
                                                    >
                                                        {copiedWelcome ? <Check size={18} /> : <Copy size={18} />}
                                                        {copiedWelcome ? 'Copied!' : 'Copy Message'}
                                                    </button>
                                                </div>

                                                <div className="flex-1 w-full">
                                                    <div className={`p-6 rounded-2xl border text-sm whitespace-pre-wrap leading-relaxed ${isLight ? 'bg-neutral-50 border-neutral-200 text-neutral-600' : 'bg-white/5 border-white/5 text-neutral-300'
                                                        }`}>
                                                        <p>Welcome!</p>
                                                        <p className="mt-4">During your stay, you'll have access to a smart digital concierge that can answer questions instantly - anytime, day or night.</p>
                                                        <p className="mt-4">From check-in & Wi-Fi to local recommendations, just tap below:</p>
                                                        <p className="mt-4 text-blue-500 break-all">https://chat.kypser.com?propertyId={displayUiId}</p>
                                                        <p className="mt-4">We're always here if you need anything.</p>
                                                        <p className="mt-4">Enjoy your stay</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Action Buttons */}
                                    <div className="flex gap-4 pt-6">
                                        <button
                                            onClick={handleBack}
                                            className={`flex-1 py-4 rounded-2xl font-bold border transition-all ${isLight ? 'bg-white border-neutral-300 hover:bg-neutral-50' : 'bg-neutral-900 border-neutral-800 hover:bg-neutral-800'
                                                }`}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleAction}
                                            disabled={loading || !formData.name || !formData.category || !formData.address}
                                            className={`flex-[2] py-4 rounded-2xl font-bold shadow-lg transition-all hover:scale-[1.01] active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${isLight ? 'bg-neutral-900 text-white shadow-neutral-900/20' : 'bg-white text-black shadow-white/10'
                                                }`}
                                        >
                                            {loading && <Loader2 size={18} className="animate-spin" />}
                                            {editingId ? 'Save Changes' : 'Create Property'}
                                        </button>
                                    </div>

                                    {/* Danger Zone - Delete Property */}
                                    {editingId && (
                                        <div className={`pt-8 mt-4 border-t ${isLight ? 'border-neutral-200' : 'border-white/5'}`}>
                                            <div className={`p-4 rounded-2xl border flex items-center justify-between ${isLight ? 'bg-red-50 border-red-100' : 'bg-red-500/5 border-red-500/10'}`}>
                                                <div>
                                                    <h4 className="text-sm font-bold text-red-500">Delete Property</h4>
                                                    <p className="text-xs text-red-500/70 mt-1">Permanently remove this property and all its data</p>
                                                </div>
                                                <button
                                                    onClick={() => {
                                                        const property = properties.find(p => p.id === editingId);
                                                        if (property) openDeleteModal(property);
                                                    }}
                                                    className="p-2.5 rounded-xl bg-red-500 text-white shadow-lg shadow-red-500/20 active:scale-95 transition-all"
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>


                        </div >
                    </div>
                </div>
            </>
        );
    }

    // Portfolio View
    return (
        <>
            <style>{animationStyles}</style>
            <div className="h-full flex flex-col">
                {/* Back Button - fixed above scroll area */}
                <div className="max-w-6xl mx-auto w-full px-0 sm:px-0 py-4">
                    <button
                        onClick={() => setSubview('selection')}
                        className={`flex items-center gap-2 font-bold text-sm transition-colors ${isLight ? 'text-neutral-500 hover:text-neutral-900' : 'text-neutral-400 hover:text-white'
                            }`}
                    >
                        <ArrowLeft size={16} /> Back
                    </button>
                </div>

                {/* Scrollable content area */}
                <div className="flex-1 min-h-0 overflow-y-auto hidden-scrollbar">
                    <div className="max-w-6xl mx-auto px-0 sm:px-0 pb-24 relative anim-slide-right">
                        <div className="pt-4">

                            {loading ? (
                                <div className="flex items-center justify-center py-20">
                                    <Loader2 size={48} className={`animate-spin ${isLight ? 'text-neutral-400' : 'text-neutral-600'}`} />
                                </div>
                            ) : properties.length === 0 ? (
                                <div
                                    onClick={() => setSubview('register')}
                                    className={`group cursor-pointer flex flex-col items-center justify-center p-20 rounded-3xl border-4 border-dashed transition-all hover:scale-[1.01] ${isLight ? 'bg-white border-neutral-200 hover:border-neutral-900' : 'bg-white/5 border-white/5 hover:border-white'
                                        }`}
                                >
                                    <div className={`h-20 w-20 rounded-full flex items-center justify-center mb-6 transition-transform group-hover:rotate-12 ${isLight ? 'bg-neutral-900 text-white' : 'bg-white text-black'
                                        }`}>
                                        <Plus size={32} />
                                    </div>
                                    <h3 className={`text-2xl font-bold mb-2 ${isLight ? 'text-neutral-900' : 'text-white'}`}>
                                        No properties yet
                                    </h3>
                                    <p className={`font-semibold ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                                        Click here to register your first location
                                    </p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 gap-3 px-4 sm:px-0">
                                    {properties.map(p => (
                                        <div
                                            key={p.id}
                                            className={`group flex flex-col p-4 sm:p-8 rounded-3xl border transition-all ${isLight ? 'bg-white border-neutral-200 shadow-sm hover:shadow-md' : 'bg-white/5 border-white/5 hover:bg-white/10'
                                                }`}
                                        >
                                            <div
                                                onClick={() => startEdit(p)}
                                                className="flex items-center justify-between cursor-pointer mb-4"
                                            >
                                                <div className="flex items-center gap-4 sm:gap-8 flex-1 min-w-0">
                                                    <div className={`h-12 w-12 sm:h-16 sm:w-16 rounded-full flex-shrink-0 overflow-hidden border flex items-center justify-center ${isLight ? 'bg-neutral-100 border-neutral-200' : 'bg-black border-white/10'
                                                        }`}>
                                                        {renderAvatarContent(p.hotel_logo_url, 'sm')}
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <h3 className={`font-bold text-lg sm:text-2xl leading-tight ${isLight ? 'text-neutral-900' : 'text-white'}`}>
                                                            {p.name}
                                                        </h3>
                                                        <p className={`text-xs sm:text-sm mt-1 font-medium ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                                                            {p.address ? `${p.address}, ` : ''}{p.city} • {p.category || p.type}
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="pl-4">
                                                    <button
                                                        className={`p-2.5 sm:p-4 rounded-xl sm:rounded-2xl border transition-all hover:scale-110 ${isLight ? 'border-neutral-200 hover:bg-neutral-900 hover:text-white' : 'border-white/10 hover:bg-white hover:text-black'
                                                            }`}
                                                    >
                                                        <Pencil size={18} className="sm:w-5 sm:h-5" />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Share Actions */}
                                            <div className={`pt-4 border-t ${isLight ? 'border-neutral-200' : 'border-white/10'}`}>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            // Copy welcome message for this property
                                                            const link = `https://chat.kypser.com?propertyId=${p.ui_id}`;
                                                            const message = `Welcome!

During your stay, you'll have access to a smart digital concierge that can answer questions instantly - anytime, day or night.

From check-in & Wi-Fi to local recommendations, just tap below:

${link}

We're always here if you need anything.

Enjoy your stay`;
                                                            navigator.clipboard.writeText(message);
                                                            setCopiedWelcome(p.id);
                                                            setTimeout(() => setCopiedWelcome(false), 2000);
                                                        }}
                                                        className={`flex items-center gap-2 px-4 py-3 rounded-xl border transition-all hover:scale-[1.02] active:scale-95 justify-center ${copiedWelcome === p.id
                                                            ? isLight
                                                                ? 'bg-green-50 border-green-500 text-green-600'
                                                                : 'bg-green-500/10 border-green-500 text-green-400'
                                                            : isLight
                                                                ? 'bg-neutral-50 border-neutral-200 hover:border-neutral-900'
                                                                : 'bg-white/5 border-white/10 hover:border-white'
                                                            }`}
                                                    >
                                                        {copiedWelcome === p.id ? <Check size={16} /> : <Copy size={16} />}
                                                        <span className={`text-sm font-bold ${copiedWelcome === p.id ? '' : isLight ? 'text-neutral-900' : 'text-white'}`}>
                                                            {copiedWelcome === p.id ? 'Copied!' : 'Welcome Message'}
                                                        </span>
                                                    </button>

                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            copyLink(p.ui_id);
                                                        }}
                                                        className={`flex items-center gap-2 px-4 py-3 rounded-xl border transition-all hover:scale-[1.02] active:scale-95 justify-center ${copiedLink === p.ui_id
                                                            ? isLight
                                                                ? 'bg-green-50 border-green-500 text-green-600'
                                                                : 'bg-green-500/10 border-green-500 text-green-400'
                                                            : isLight
                                                                ? 'bg-neutral-50 border-neutral-200 hover:border-neutral-900'
                                                                : 'bg-white/5 border-white/10 hover:border-white'
                                                            }`}
                                                    >
                                                        {copiedLink === p.ui_id ? <Check size={16} /> : <Copy size={16} />}
                                                        <span className={`text-sm font-bold ${copiedLink === p.ui_id ? '' : isLight ? 'text-neutral-900' : 'text-white'}`}>
                                                            {copiedLink === p.ui_id ? 'Copied!' : 'Chatbot Link'}
                                                        </span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

function SelectionCard({ isLight, icon, title, desc, onClick, badge }) {
    return (
        <button
            onClick={onClick}
            className={`group relative p-6 sm:p-10 rounded-2xl sm:rounded-3xl border text-left flex flex-col items-start space-y-4 sm:space-y-6 shadow-xl ${isLight ? 'bg-white border-neutral-200' : 'bg-[#12151a] border-neutral-800'
                }`}
        >
            {badge && (
                <span className={`absolute top-4 right-4 sm:top-8 sm:right-8 px-2 sm:px-3 py-1 text-[10px] sm:text-xs font-bold rounded-full ${isLight ? 'bg-neutral-900 text-white' : 'bg-white text-black'
                    }`}>
                    {badge}
                </span>
            )}
            <div className={`h-14 w-14 sm:h-20 sm:w-20 rounded-full flex items-center justify-center ${isLight ? 'bg-neutral-900 text-white shadow-lg' : 'bg-white text-black shadow-lg'
                }`}>
                {React.cloneElement(icon, { size: undefined, className: 'w-6 h-6 sm:w-10 sm:h-10' })}
            </div>
            <div>
                <h3 className={`text-xl sm:text-2xl font-bold mb-2 sm:mb-3 ${isLight ? 'text-neutral-900' : 'text-white'}`}>
                    {title}
                </h3>
                <p className={`text-xs sm:text-sm font-semibold ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                    {desc}
                </p>
            </div>
        </button>
    );
}

function FormInput({ label, value, onChange, isLight, placeholder }) {
    return (
        <div className="space-y-3">
            <label className={`text-xs uppercase tracking-wider font-bold px-1 ${isLight ? 'text-neutral-400' : 'text-neutral-500'
                }`}>
                {label}
            </label>
            <input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className={`w-full px-5 py-4 rounded-2xl border bg-transparent font-semibold outline-none transition-all ${isLight ? 'border-neutral-300 focus:border-neutral-900 text-neutral-900' : 'border-white/10 focus:border-white text-white'
                    }`}
            />
        </div>
    );
}

export default PropertiesManagement;

